import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { LucideModule } from '../../../shared/modules/lucide.module';
import { AlertaService } from '../../../shared/utils/alerta.service';
import { AvaliacaoAtendimentoService } from '../services/avaliacao-atendimento.service';

type RatingItem = { value: number; emoji: string; label: string };

const TEMPO_LIMITE = 5 * 60 * 1000;
const CHAVE_ULTIMA_AVALIACAO = 'ultima_avaliacao';

const ROTAS_OCULTAS = ['/area-cidadao', '/homepage', '/login', '/cadastro', '/'];

@Component({
  selector: 'app-avaliacao-atendimento',
  imports: [LucideModule, RouterLink],
  templateUrl: './avaliacao-atendimento.component.html',
  styleUrls: ['./avaliacao-atendimento.component.css'],
  host: {
    '(document:keydown.escape)': 'fecharModal()',
  },
})
export class AvaliacaoAtendimentoComponent implements OnInit {

  private readonly avaliacaoService = inject(AvaliacaoAtendimentoService);
  private readonly alerta = inject(AlertaService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private readonly urlAtual = toSignal(
    this.router.events.pipe(
      filter((evento) => evento instanceof NavigationEnd),
      map(() => this.router.url)
    ),
    { initialValue: this.router.url }
  );

  readonly visivel = computed(() => {
    const url = this.urlAtual().split('?')[0];
    return !ROTAS_OCULTAS.some((rota) => url === rota || url.startsWith(`${rota}/`));
  });

  /** Na Ouvidoria o atalho leva ao SIC; nas demais telas, incluindo o SIC, leva à Ouvidoria. */
  readonly atalho = computed(() =>
    this.urlAtual().split('?')[0].startsWith('/ouvidoria')
      ? { link: '/sic', texto: 'Ir para SIC' }
      : { link: '/ouvidoria', texto: 'Ir para Ouvidoria' }
  );

  readonly modalAberto = signal(false);
  readonly botaoDesabilitado = signal(false);
  readonly selectedRate = signal<number | null>(null);
  readonly comentario = signal('');

  readonly formularioValido = computed(() => this.selectedRate() !== null);

  private timeoutId?: ReturnType<typeof setTimeout>;

  readonly ratings: RatingItem[] = [
    { value: 0, emoji: '😭', label: 'Péssimo (0)' },
    { value: 1, emoji: '😢', label: 'Ruim (1)' },
    { value: 2, emoji: '😕', label: 'Regular (2)' },
    { value: 3, emoji: '😐', label: 'Ok (3)' },
    { value: 4, emoji: '🙂', label: 'Bom (4)' },
    { value: 5, emoji: '🤩', label: 'Excelente (5)' },
  ];

  ngOnInit(): void {
    this.verificarTempoLocalStorage();
    this.destroyRef.onDestroy(() => this.limparTemporizador());
  }

  private verificarTempoLocalStorage(): void {
    const ultimaAvaliacao = localStorage.getItem(CHAVE_ULTIMA_AVALIACAO);
    if (!ultimaAvaliacao) return;

    const tempoDecorrido = Date.now() - Number(ultimaAvaliacao);

    if (tempoDecorrido < TEMPO_LIMITE) {
      this.botaoDesabilitado.set(true);
      this.agendarLiberacao(TEMPO_LIMITE - tempoDecorrido);
      return;
    }

    localStorage.removeItem(CHAVE_ULTIMA_AVALIACAO);
    this.botaoDesabilitado.set(false);
  }

  /**
   * Envia a avaliacao. **Tres campos, e so**: nota, comentario e o CAMINHO da pagina.
   *
   * O que saiu daqui em 14/10/2026, e por que:
   *
   * - **o IP** (`obterIpCliente()` e a chamada ao `api.ipify.org`): IP ao lado de nota e
   *   comentario reconstroi a autoria que a avaliacao promete nao ter — e pedi-lo a um
   *   terceiro contava a um servico externo que aquele visitante estava avaliando o portal.
   *   Com ele saiu a dependencia do `ConsentService` neste fluxo;
   * - **o orgao** (`codigoOrgao()`): vinha do `localStorage` numa rota anonima, o que deixaria
   *   qualquer um gravar avaliacao no orgao que quisesse, e um visitante deslogado gravaria no
   *   orgao `NaN`. Agora o back-end o resolve pelo **host** do portal;
   * - **`location.href`**: `href` carrega query string, e uma avaliacao enviada de
   *   `/acompanhamento?protocolo=...` gravaria o protocolo de um pedido ao lado da nota. Vai
   *   so o `pathname` — e o servidor normaliza de novo, porque nao confia no cliente.
   */
  async enviarAvaliacao(): Promise<void> {
    const nota = this.selectedRate();
    if (nota === null) return;

    const comentario = this.comentario().trim();

    this.avaliacaoService
      .registrar({
        nota,
        comentario: comentario.length > 0 ? comentario : null,
        caminho: window.location.pathname,
      })
      .subscribe({
        next: async () => {
          this.botaoDesabilitado.set(true);
          localStorage.setItem(CHAVE_ULTIMA_AVALIACAO, Date.now().toString());
          this.agendarLiberacao(TEMPO_LIMITE);

          this.fecharModal();

          await this.alerta.sucesso({
            titulo: 'Obrigado!',
            texto: 'Sua avaliação é muito importante para melhorarmos nossos serviços.',
            confirmar: 'Fechar',
          });
        },
        error: () => {
          this.alerta.erro({ titulo: 'Erro', texto: 'Erro ao enviar avaliação' });
        },
      });
  }

  abrirModal(): void {
    if (this.botaoDesabilitado()) return;
    this.modalAberto.set(true);
  }

  fecharModal(): void {
    if (!this.modalAberto()) return;
    this.modalAberto.set(false);
    this.resetar();
  }

  selectRate(value: number): void {
    this.selectedRate.set(value);
  }

  aoDigitarComentario(evento: Event): void {
    this.comentario.set((evento.target as HTMLTextAreaElement).value);
  }

  private agendarLiberacao(emMilissegundos: number): void {
    this.limparTemporizador();
    this.timeoutId = setTimeout(() => {
      this.botaoDesabilitado.set(false);
      localStorage.removeItem(CHAVE_ULTIMA_AVALIACAO);
    }, emMilissegundos);
  }

  private limparTemporizador(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }
  }

  private resetar(): void {
    this.selectedRate.set(null);
    this.comentario.set('');
  }
}
